/**
 * Origine HTTP du backend (sans /api).
 * En dev : chaîne vide → requêtes relatives passées par le proxy CRA (pas de CORS).
 */
export function getApiOrigin() {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv) {
    return fromEnv.replace(/\/$/, '');
  }
  return '';
}

/** Préfixe axios : `/api` en dev (proxy) ou URL absolue en prod. */
export function getApiBasePath() {
  const origin = getApiOrigin();
  return origin ? `${origin}/api` : '/api';
}

/** URL absolue ou relative vers une route API (ex. `/login`, `/images/...`). */
export function apiPath(relativePath) {
  const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
  return `${getApiBasePath()}${path}`;
}

/**
 * En dev, les URLs `http://localhost:8000/storage/...` passent par le proxy CRA via `/storage/...`.
 */
export function normalizeStorageUrl(url) {
  if (!url || typeof url !== 'string') {
    return url;
  }
  if (url.startsWith('/storage/') || !url.startsWith('http')) {
    return url;
  }
  if (process.env.NODE_ENV !== 'development') {
    return url;
  }
  try {
    const { pathname, search } = new URL(url);
    if (pathname.startsWith('/storage/')) {
      return `${pathname}${search}`;
    }
  } catch {
    return url;
  }
  return url;
}

const preloadedImages = new Set();
const documentPreloaded = new Set();

const injectDocumentPreload = (src, options = {}) => {
  if (!src || typeof document === 'undefined' || documentPreloaded.has(src)) {
    return;
  }
  documentPreloaded.add(src);
  if (document.querySelector(`link[rel="preload"][href="${CSS.escape(src)}"]`)) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = src;
  link.fetchPriority = options.fetchPriority || 'high';
  if (src.includes('.webp')) {
    link.type = 'image/webp';
  }
  if (options.imageSrcSet) {
    link.setAttribute('imagesrcset', options.imageSrcSet);
  }
  if (options.imageSizes) {
    link.setAttribute('imagesizes', options.imageSizes);
  }
  document.head.appendChild(link);
};

const preloadImage = (src, options = {}) => {
  if (!src) {
    return Promise.resolve(false);
  }
  injectDocumentPreload(src, options);
  if (preloadedImages.has(src)) {
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    const img = new Image();
    if (options.fetchPriority) {
      img.fetchPriority = options.fetchPriority;
    }
    img.onload = () => {
      preloadedImages.add(src);
      resolve(true);
    };
    img.onerror = () => resolve(false);
    img.src = src;
    if (img.complete && img.naturalWidth > 0) {
      preloadedImages.add(src);
      resolve(true);
    }
  });
};

const isImagePreloaded = (src) => preloadedImages.has(src);

export { preloadImage, isImagePreloaded, injectDocumentPreload };
